import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Music, Mic2, Send, Loader2, CheckCircle2, PartyPopper } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const VERSIONI = [
  "Originale",
  "Acustica",
  "Duetto",
  "Karaoke / Base",
  "Remix / Dance",
  "Live",
  "Altro",
];

const empty = {
  nome: "",
  cognome: "",
  titolo: "",
  artista: "",
  versione: "Originale",
  tavolo: "",
  note: "",
  evento: "",
};

export default function BookingPage() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [eventName, setEventName] = useState("Karaoke Night");
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api
      .get("/event")
      .then(({ data }) => setEventName(data.event_name))
      .catch(() => {});
    api
      .get("/events")
      .then(({ data }) => setEvents(data))
      .catch(() => {});
    api
      .get("/active-event")
      .then(({ data }) => {
        if (data.active_event) setForm((f) => ({ ...f, evento: data.active_event }));
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.titolo || !form.artista) {
      toast.error("Compila Nome, Cognome, Titolo e Artista");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Canzone prenotata! 🎤");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const another = () => {
    setForm(empty);
    setDone(false);
  };

  return (
    <div className="neon-grid min-h-screen w-full">
      {/* header */}
      <header className="glass sticky top-0 z-20 border-b border-slate-800/80">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="relative">
              <Mic2 className="h-6 w-6 text-pink-500" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight text-white">
              DJ<span className="text-pink-500">KARAOKE</span>
            </span>
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 flex items-center gap-2">
            <span className="relative inline-flex h-2 w-2">
              <span className="live-dot absolute inline-flex h-2 w-2" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            Live
          </span>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8 sm:py-12">
        {!done ? (
          <>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8 text-center"
            >
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-pink-400 mb-3">
                {eventName}
              </p>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-white text-glow leading-tight">
                Prenota la tua
                <br />
                canzone
              </h1>
              <p className="mt-4 text-slate-400 text-base">
                Sali sul palco stasera. Scegli il brano e il DJ ti chiamerà 🎶
              </p>
            </motion.div>

            <motion.form
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              onSubmit={submit}
              className="neon-card rounded-2xl p-5 sm:p-7 space-y-5"
            >
              <div className="grid grid-cols-2 gap-4">
                <Field label="Nome" testid="booking-form-nome-input" value={form.nome} onChange={set("nome")} placeholder="Marco" />
                <Field label="Cognome" testid="booking-form-cognome-input" value={form.cognome} onChange={set("cognome")} placeholder="Rossi" />
              </div>

              {events.length > 0 && (
                <div>
                  <Label>Serata / Evento</Label>
                  <select
                    data-testid="booking-form-evento-select"
                    value={form.evento}
                    onChange={set("evento")}
                    className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/30 outline-none px-4 py-3 text-white transition-colors"
                  >
                    <option value="" className="bg-slate-900">Seleziona la serata…</option>
                    {events.map((e) => (
                      <option key={e.id} value={e.title} className="bg-slate-900">
                        {e.title}{e.place ? ` — ${e.place}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <Field
                label="Titolo Canzone"
                testid="booking-form-titolo-input"
                value={form.titolo}
                onChange={set("titolo")}
                placeholder="Bohemian Rhapsody"
                icon={<Music className="h-4 w-4 text-pink-400" />}
              />
              <Field
                label="Artista"
                testid="booking-form-artista-input"
                value={form.artista}
                onChange={set("artista")}
                placeholder="Queen"
              />

              <div>
                <Label>Versione</Label>
                <select
                  data-testid="booking-form-versione-select"
                  value={form.versione}
                  onChange={set("versione")}
                  className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/30 outline-none px-4 py-3 text-white transition-colors"
                >
                  {VERSIONI.map((v) => (
                    <option key={v} value={v} className="bg-slate-900">
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <Field
                label="Numero Tavolo (opzionale)"
                testid="booking-form-tavolo-input"
                value={form.tavolo}
                onChange={set("tavolo")}
                placeholder="Es. 12"
              />

              <div>
                <Label>Note (opzionale)</Label>
                <textarea
                  data-testid="booking-form-note-input"
                  value={form.note}
                  onChange={set("note")}
                  rows={2}
                  placeholder="Dediche, tonalità, richieste speciali…"
                  className="w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/30 outline-none px-4 py-3 text-white transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="booking-form-submit-button"
                disabled={submitting}
                className="w-full beam text-white font-semibold rounded-xl py-3.5 flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition disabled:opacity-60"
              >
                {submitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Prenota la canzone
                  </>
                )}
              </button>
            </motion.form>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="neon-card rounded-2xl p-8 text-center mt-10"
            data-testid="booking-success"
          >
            <div className="flex justify-center mb-4">
              <PartyPopper className="h-14 w-14 text-pink-500" />
            </div>
            <h2 className="font-display text-3xl font-black text-white text-glow">
              Sei in lista!
            </h2>
            <p className="mt-3 text-slate-300">
              <span className="text-pink-400 font-semibold">
                {form.titolo}
              </span>{" "}
              di {form.artista} è stata inviata al DJ.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-emerald-400 text-sm">
              <CheckCircle2 className="h-4 w-4" /> Preparati a cantare!
            </div>
            <button
              onClick={another}
              data-testid="booking-another-button"
              className="mt-8 w-full border border-slate-700 hover:border-pink-500 text-white rounded-xl py-3 transition-colors"
            >
              Prenota un'altra canzone
            </button>
          </motion.div>
        )}
      </main>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-2">
      {children}
    </label>
  );
}

function Field({ label, testid, value, onChange, placeholder, icon }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        {icon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2">{icon}</span>
        )}
        <input
          data-testid={testid}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full rounded-xl bg-slate-950/60 border border-slate-700 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/30 outline-none py-3 text-white placeholder:text-slate-600 transition-colors ${
            icon ? "pl-10 pr-4" : "px-4"
          }`}
        />
      </div>
    </div>
  );
}
